import {
    AppwriteBatchSetRequest, Collection, generateId, InventoryTransaction, InventoryTransactionType,
    ObsessedEngineeringComponent, ObsessedEngineeringProduct, ObsessedEngineeringProductBomLineItem,
    ObsessedEngineeringProductVersion, toAppwrite,
} from '@prism/prism-shared';

export interface OeProductInventoryBatchInput {
    product: ObsessedEngineeringProduct;
    version: ObsessedEngineeringProductVersion;
    bom: ObsessedEngineeringProductBomLineItem[];
    components: ObsessedEngineeringComponent[];
    actorId: string;
    change: number;
    comment: string;
    adjustBomInventory: boolean;
}

export function createOeProductInventoryBatch(
    input: OeProductInventoryBatchInput,
    createId: () => string = generateId,
): { request: AppwriteBatchSetRequest; transaction: InventoryTransaction } {
    const { product, version, actorId, change, comment } = input;
    if (!actorId) throw new Error('Sign in to continue.');
    if (!product || !version || version.productId !== product.id) throw new Error('Choose a version belonging to this OE product.');
    if (!Number.isSafeInteger(change) || !change || typeof comment !== 'string') throw new Error('Enter a nonzero whole inventory quantity.');
    const componentChanges = new Map<string, number>();
    if (input.adjustBomInventory) {
        for (const line of input.bom) {
            if (!line.componentId || !Number.isFinite(line.quantity) || line.quantity <= 0) throw new Error('The BOM contains an invalid component or quantity.');
            const componentChange = (componentChanges.get(line.componentId) ?? 0) - change * line.quantity;
            if (!Number.isFinite(componentChange)) throw new Error('The BOM inventory quantity is too large.');
            componentChanges.set(line.componentId, componentChange);
        }
    }
    const byId = new Map(input.components.map(component => [component.id, component]));
    const components = [...componentChanges.keys()].map(id => {
        const component = byId.get(id);
        if (!component) throw new Error(`Unable to load BOM component: ${id}`);
        return component;
    });
    const createdOn = new Date();
    const transactionGroupId = createId();
    const transaction: InventoryTransaction = {
        id: createId(), entityId: product.id, change, comment, createdOn, createdById: actorId,
        type: InventoryTransactionType.ObsessedEngineeringProduct, transactionGroupId,
    };
    const history: InventoryTransaction[] = [transaction, ...components.map(component => ({
        id: createId(), entityId: component.id, change: componentChanges.get(component.id)!,
        comment: `BOM inventory for ${version.partNumber}: ${comment}`, createdOn, createdById: actorId,
        type: InventoryTransactionType.ObsessedEngineeringComponent, transactionGroupId,
    }))];
    const quantityRow = (entity: { id: string; qtyOnHand: number }, delta: number) => {
        const qtyOnHand = entity.qtyOnHand + delta;
        if (!Number.isFinite(entity.qtyOnHand) || !Number.isFinite(qtyOnHand)) throw new Error('An OE inventory quantity is invalid.');
        return { id: entity.id, data: { qtyOnHand }, expected: { qtyOnHand: entity.qtyOnHand } };
    };
    const request: AppwriteBatchSetRequest = { groups: [
        { collection: Collection.obsessedEngineeringProduct, action: 'upsert', rows: [quantityRow(product, change)] },
        ...(components.length ? [{
            collection: Collection.obsessedEngineeringComponent, action: 'upsert' as const,
            rows: components.map(component => quantityRow(component, componentChanges.get(component.id)!)),
        }] : []),
        { collection: Collection.inventoryTransaction, action: 'create', rows: history.map(row => ({
            id: row.id, data: toAppwrite(Collection.inventoryTransaction, row),
        })) },
    ] };
    return { request, transaction };
}
